import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumbs from "./components/Breadcrumbs";
import FormInput from "./components/FormInput";
import FormSelect from "./components/FormSelect";
import FormToggle from "./components/FormToggle";
import ActionButton from "./components/ActionButton";
import Toast from "./components/Toast";
import ConfirmationModal from "./components/ConfirmationModal";
import Icon from "../../components/AppIcon";

const FormEditor = () => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    status: "draft",
    priority: "medium",
    dueDate: "",
    assignedTo: "",
    isPublic: false,
  });
  
  const [initialFormData, setInitialFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [navigateTo, setNavigateTo] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);

  // Mock data for dropdowns
  const categories = [
    { value: "task", label: "Task" },
    { value: "bug", label: "Bug" },
    { value: "feature", label: "Feature" },
    { value: "improvement", label: "Improvement" },
  ];

  const priorities = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "critical", label: "Critical" },
  ];

  const statuses = [
    { value: "draft", label: "Draft" },
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
  ];

  const users = [
    { value: "user1", label: "John Doe" },
    { value: "user2", label: "Jane Smith" },
    { value: "user3", label: "Robert Johnson" },
    { value: "user4", label: "Emily Davis" },
  ];

  // Simulate fetching data for edit mode
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get("id");
    
    if (id) {
      setIsEditing(true);
      // Simulate API call to fetch item details
      setTimeout(() => {
        const mockItem = {
          id: id,
          title: "Implement User Authentication",
          description: "Add user authentication functionality with email and password login, as well as social login options.",
          category: "feature",
          status: "in_progress",
          priority: "high",
          dueDate: "2023-12-31",
          assignedTo: "user2",
          isPublic: true,
        };
        
        setFormData(mockItem);
        setInitialFormData(mockItem);
        setLastSaved(new Date());
      }, 500);
    } else {
      setInitialFormData({...formData});
    }
  }, []);

  // Validate form fields
  const validateField = (name, value) => {
    let error = "";
    
    switch (name) {
      case "title":
        if (!value.trim()) {
          error = "Title is required";
        } else if (value.length < 3) {
          error = "Title must be at least 3 characters";
        }
        break;
      case "description":
        if (!value.trim()) {
          error = "Description is required";
        }
        break;
      case "category":
        if (!value) {
          error = "Category is required";
        }
        break;
      case "dueDate":
        if (!value) {
          error = "Due date is required";
        } else {
          const selectedDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (selectedDate < today) {
            error = "Due date cannot be in the past";
          }
        }
        break;
      case "assignedTo":
        if (!value) {
          error = "Assignee is required";
        }
        break;
      default:
        break;
    }
    
    return error;
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  // Handle input blur for validation
  const handleBlur = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;
    
    const error = validateField(name, fieldValue);
    
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Validate all form fields
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    Object.entries(formData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    return isValid;
  };

  // Auto-save functionality
  const autoSave = useCallback(() => {
    if (autoSaveEnabled && JSON.stringify(formData) !== JSON.stringify(initialFormData)) {
      console.log("Auto-saving form data...");
      // Simulate API call to save draft
      setTimeout(() => {
        setLastSaved(new Date());
        setInitialFormData({...formData});
        
        setToastMessage("Draft saved automatically");
        setToastType("info");
        setShowToast(true);
      }, 500);
    }
  }, [formData, initialFormData, autoSaveEnabled]);

  // Set up auto-save interval
  useEffect(() => {
    const interval = setInterval(autoSave, 30000); // Auto-save every 30 seconds
    
    return () => clearInterval(interval);
  }, [autoSave]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setToastMessage("Please fix the errors in the form");
      setToastType("error");
      setShowToast(true);
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setToastMessage(isEditing ? "Item updated successfully" : "Item created successfully");
      setToastType("success");
      setShowToast(true);
      
      // Redirect after successful submission
      setTimeout(() => {
        navigate("/data-table-view");
      }, 1000);
    } catch (error) {
      setToastMessage("An error occurred. Please try again.");
      setToastType("error");
      setShowToast(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle navigation with unsaved changes
  const handleNavigation = (path) => {
    if (JSON.stringify(formData) !== JSON.stringify(initialFormData)) {
      setShowConfirmModal(true);
      setNavigateTo(path);
    } else {
      navigate(path);
    }
  };

  // Confirm navigation and discard changes
  const confirmNavigation = () => {
    setShowConfirmModal(false);
    navigate(navigateTo);
  };

  // Cancel navigation
  const cancelNavigation = () => {
    setShowConfirmModal(false);
    setNavigateTo("");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <div className="flex flex-1">
        <Sidebar 
          variant="with-categories" 
          isCollapsed={isCollapsed}
          onToggle={setIsCollapsed}
        />
        
        <main 
          className={`flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'} md:ml-64`}
          id="main-content"
        >
          <div className="py-6 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumbs */}
            <Breadcrumbs 
              items={[
                { label: "Dashboard", path: "/dashboard-overview" },
                { label: "Data Table", path: "/data-table-view" },
                { label: isEditing ? "Edit Item" : "New Item", path: "/form-editor", current: true }
              ]} 
            />
            
            {/* Page header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mt-2 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {isEditing ? "Edit Item" : "Create New Item"}
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  {isEditing 
                    ? "Update the information for this item" 
                    : "Fill in the details to create a new item"}
                </p>
              </div>
              
              {lastSaved && (
                <div className="mt-2 md:mt-0 text-sm text-gray-500 flex items-center">
                  <Icon name="Save" size={16} className="mr-1" />
                  Last saved: {lastSaved.toLocaleTimeString()}
                </div>
              )}
            </div>
            
            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6 pb-20">
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <div className="p-6 space-y-6">
                  {/* Title */}
                  <FormInput
                    label="Title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.title}
                    placeholder="Enter a title"
                    required
                  />
                  
                  {/* Description */}
                  <FormInput
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.description}
                    placeholder="Enter a detailed description"
                    multiline
                    rows={4}
                    required
                  />
                  
                  {/* Category */}
                  <FormSelect
                    label="Category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.category}
                    options={categories}
                    placeholder="Select a category"
                    required
                  />
                  
                  {/* Status */}
                  <FormSelect
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.status}
                    options={statuses}
                    placeholder="Select a status"
                    required
                  />
                  
                  {/* Priority */}
                  <FormSelect
                    label="Priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.priority}
                    options={priorities}
                    placeholder="Select a priority"
                    required
                  />
                  
                  {/* Due Date */}
                  <FormInput
                    label="Due Date"
                    name="dueDate"
                    type="date"
                    value={formData.dueDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.dueDate}
                    required
                  />
                  
                  {/* Assigned To */}
                  <FormSelect
                    label="Assigned To"
                    name="assignedTo"
                    value={formData.assignedTo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.assignedTo}
                    options={users}
                    placeholder="Select an assignee"
                    required
                  />
                  
                  {/* Public Toggle */}
                  <FormToggle
                    label="Make Public"
                    description="When enabled, this item will be visible to all users"
                    name="isPublic"
                    checked={formData.isPublic}
                    onChange={handleChange}
                  />
                  
                  {/* Auto-save Toggle */}
                  <FormToggle
                    label="Enable Auto-save"
                    description="Automatically save your changes every 30 seconds"
                    name="autoSaveEnabled"
                    checked={autoSaveEnabled}
                    onChange={() => setAutoSaveEnabled(!autoSaveEnabled)}
                  />
                </div>
              </div>
            </form>
            
            {/* Fixed action buttons */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:px-8">
              <div className="max-w-4xl mx-auto flex justify-end space-x-4">
                <ActionButton
                  variant="secondary"
                  onClick={() => handleNavigation("/data-table-view")}
                  disabled={isSubmitting}
                >
                  Cancel
                </ActionButton>
                <ActionButton
                  variant="primary"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {isEditing ? "Update" : "Create"}
                </ActionButton>
              </div>
            </div>
          </div>
        </main>
      </div>
      
      {/* Toast notification */}
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}
      
      {/* Confirmation modal for unsaved changes */}
      {showConfirmModal && (
        <ConfirmationModal
          title="Unsaved Changes"
          message="You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost."
          confirmText="Leave Page"
          cancelText="Stay"
          onConfirm={confirmNavigation}
          onCancel={cancelNavigation}
        />
      )}
    </div>
  );
};

export default FormEditor;